import { useTranslation } from 'react-i18next';

import type { CsvDelimiter } from './card-batch';
import { CSV_DELIMITER_OPTIONS, looksMisparsed } from './card-batch';

export function CsvPreviewSection({
  delimiter,
  isAuto,
  onDelimiterChange,
  columns,
  rawColumns,
  rows,
  totalRows,
}: {
  delimiter: CsvDelimiter;
  isAuto: boolean;
  onDelimiterChange: (d: CsvDelimiter) => void;
  columns: string[];
  /** The header unmodified, so the separator check can tell quoting apart. */
  rawColumns: string[];
  rows: string[][];
  totalRows: number;
}) {
  const { t } = useTranslation();
  const hasUpn = columns.some((c) => c.toLowerCase() === 'upn');
  const badParsing = looksMisparsed(rawColumns, delimiter);

  return (
    <div className="mt-2 overflow-hidden border border-rule text-xs">
      {/* Delimiter picker */}
      <div className="flex flex-wrap items-center gap-2 border-b border-b-rule bg-ink/3 px-3 py-2">
        <span className="shrink-0 text-muted-soft">
          {t('cardGenerator.csvDelimiter.label')} :
        </span>
        <div className="flex overflow-hidden border border-rule">
          {CSV_DELIMITER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onDelimiterChange(opt.value)}
              className="px-2.5 py-1 font-mono transition-colors"
              style={
                delimiter === opt.value
                  ? {
                      background: 'var(--blue)',
                      color: 'var(--inverse-fg)',
                      borderRight: '1px solid var(--rule)',
                    }
                  : {
                      background: 'var(--card)',
                      color: 'var(--muted)',
                      borderRight: '1px solid var(--rule)',
                    }
              }
              title={t(`cardGenerator.csvDelimiter.${opt.key}`)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {isAuto && (
          <span className="shrink-0 border border-blue bg-blue/6 px-2 py-0.5 text-blue">
            ✦ {t('cardGenerator.csvDelimiter.auto')}
          </span>
        )}
      </div>

      {/* Preview table */}
      {badParsing ? (
        <div className="flex items-center gap-1.5 px-3 py-2 text-magenta">
          <span>✗</span>
          <span>{t('cardGenerator.csvDelimiter.badParsing')}</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-ink/3">
                {columns.map((col) => (
                  <th
                    key={col}
                    className="border-b border-b-rule px-3 py-1.5 text-left font-medium whitespace-nowrap text-muted"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.join('')}
                  style={
                    i % 2 === 1
                      ? { background: 'rgba(24,24,24,0.02)' }
                      : undefined
                  }
                >
                  {columns.map((col, j) => (
                    <td
                      key={col}
                      title={row[j] ?? ''}
                      className="max-w-[160px] truncate border-b border-b-rule px-3 py-1 text-ink-soft"
                    >
                      {row[j] ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
              {totalRows > rows.length && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-3 py-1.5 text-muted-soft italic"
                  >
                    {t('cardGenerator.csvDelimiter.moreRows', {
                      count: totalRows - rows.length,
                    })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      <div className="flex flex-wrap gap-4 border-t border-t-rule bg-ink/3 px-3 py-1.5">
        <span style={{ color: badParsing ? 'var(--magenta)' : '#007a47' }}>
          {badParsing ? '✗' : '✓'}{' '}
          {t('cardGenerator.csvCols', { count: columns.length })}
        </span>
        <span style={{ color: hasUpn ? '#007a47' : 'var(--magenta)' }}>
          {hasUpn ? '✓' : '✗'}{' '}
          {hasUpn
            ? t('cardGenerator.csvDelimiter.upnFound')
            : t('cardGenerator.csvDelimiter.upnMissing')}
        </span>
      </div>
    </div>
  );
}
